/* ÖLÇÜLEN BEDEL (2026-09-13) — bu import bir zamanlar giriş paketine `@dentos/contracts`ın zod
   şemalarını sokuyordu: `public-entry.tsx` bu dosyayı kullanır ve o da giriş grafiğindedir.
   Sabit `payments.ts`'te, 20+ `z.object(...)` ile aynı modülde durduğu için ilk boyamada çekilen
   JS 80,00 kB idi; 23,30 kB'ı yalnızca bu yüzden gelen `chunk-common`dı. `sideEffects: false` ve
   `/*#__PURE__*\/` notu açığı kapatmadı (79,75 kB ölçüldü).
   Çözüm sabiti KOPYALAMAK değildi (H20: `/odeme` tek kaynaktan okunur; elle yazılan ikinci bir
   dize hastanın eline geçen adresle sessizce ayrışabilirdi), zod'a dokunmayan kendi modülüne
   taşımaktı: `packages/contracts/src/payments-path.ts`. Sonuç 55,28 kB (−24,72 kB, H60).
   O modüle zod sızarsa bedel aynen geri gelir — kapı `scripts/assert-size-budget-covers-entry.mjs`. */
import { PUBLIC_CONSENT_PATH, PUBLIC_PAY_PATH, SURVEY_PATH } from '@dentos/contracts';

/** Prototipte URL yoktu (sayfa `page` state'i, app-v6.jsx:44). Üretimde sayfa kimlikleri yollara bağlanır (spec §3.5). */
export const PAGE_IDS = [
  'today',
  'inbox',
  'patients',
  'patient',
  'plan',
  'schedule',
  'booking',
  'lab',
  'mbys',
  'chat',
  'payments',
  'reports',
  'stock',
  'admin',
  'settings',
  'integrations',
  'einvoice',
  'automation',
  'equipment',
  'noshow',
  'recall',
  'quotes',
  'offline',
] as const;
export type PageId = (typeof PAGE_IDS)[number];

/** app-v6.jsx:93 PAGE_TITLE — birebir (breadcrumb metinleri). */
export const PAGE_TITLE: Record<PageId, string> = {
  settings: 'Ayarlar',
  einvoice: 'e-Fatura / e-SMM',
  automation: 'Otomasyon',
  equipment: 'Ünit & bakım',
  noshow: 'Gelmeme riski',
  recall: 'Recall',
  quotes: 'Teklif analitiği',
  /* v1 — sayfanın kendi başlığıyla AYNI olmalı (`OFFLINE_COPY.title`): üst çubuk "Çevrimdışı &
     yedek" derken gövde "Bağlantı & şifreli yedek" diyordu ve ekran çevrimdışı bir mod
     vadediyordu. Yolun adresi (`/cevrimdisi-yedek`) değişmedi — kayıtlı bağlantılar kırılmaz. */
  offline: 'Bağlantı & yedek',
  today: 'Bugün',
  inbox: 'Gelen kutusu',
  patients: 'Hastalar',
  patient: 'Hasta kartı',
  plan: 'Tedavi Planları',
  schedule: 'Randevular',
  booking: 'Online Randevu',
  lab: 'Laboratuvar',
  mbys: 'MBYS Asistanı',
  chat: 'WhatsApp',
  payments: 'Ödemeler',
  reports: 'Raporlar',
  stock: 'Stok',
  admin: 'Yetki & Denetim',
  integrations: 'Entegrasyonlar',
};

/** Yollar Türkçe ve kök göreli; hasta kartı /hastalar/:id[/:sekme]. */
export const PAGE_PATH: Record<Exclude<PageId, 'patient'>, string> = {
  today: '/bugun',
  inbox: '/gelen-kutusu',
  patients: '/hastalar',
  plan: '/tedavi-planlari',
  schedule: '/randevular',
  booking: '/online-randevu',
  lab: '/laboratuvar',
  mbys: '/mbys',
  chat: '/whatsapp',
  payments: '/odemeler',
  reports: '/raporlar',
  stock: '/stok',
  admin: '/yetki-denetim',
  settings: '/ayarlar',
  integrations: '/entegrasyonlar',
  einvoice: '/e-fatura',
  automation: '/otomasyon',
  equipment: '/unit-bakim',
  noshow: '/gelmeme-riski',
  recall: '/kontrol-cagrilari',
  quotes: '/teklif-analitigi',
  offline: '/cevrimdisi-yedek',
};
export const LOGIN_PATH = '/giris';
export const DEFAULT_PATIENT_TAB = 'chart';
/**
 * Hasta kartının sekmeleri (prototipin `TABS` dizisiyle aynı anahtarlar). Sürüm 2 (C169): `exam`
 * "Muayeneler" sekmesidir; sağ tık menüsünün "Muayene aç"ı kartı onunla açar.
 */
export const PATIENT_TABS = [
  'chart',
  'exam',
  'plan',
  'history',
  'notes',
  'docs',
  'consent',
  'pay',
] as const;

export interface RouteState {
  page: PageId;
  /** UUIDv7 dizesi; A2'de sayıydı (demo tohumu). */
  patientId: string | null;
  patientTab: string;
}

/** Hasta kartı yolu: /hastalar/<uuidv7>[/<sekme>]. Sayısal demo kimliği artık tanınmaz. */
/* eslint-disable security/detect-unsafe-regex -- safe-regex iç içe yıldız sanıyor: sekme grubu
   `$`e dayalı tek bir karakter sınıfıdır ve kimlik bölümü sabit uzunluktadır; geri izleme
   patlaması yok (eşleşme doğrusal). */
const PATIENT_PATH_RE =
  /^\/hastalar\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})(?:\/([a-z]+))?$/;
/* eslint-enable security/detect-unsafe-regex */

export const HOME_ROUTE: RouteState = {
  page: 'today',
  patientId: null,
  patientTab: DEFAULT_PATIENT_TAB,
};

export function pathFor(s: RouteState): string {
  if (s.page === 'patient') {
    if (s.patientId == null) return PAGE_PATH.patients;
    return `${PAGE_PATH.patients}/${s.patientId}${s.patientTab !== DEFAULT_PATIENT_TAB ? `/${s.patientTab}` : ''}`;
  }
  return PAGE_PATH[s.page];
}

/**
 * Yol → durum. `search` hasta kartının `?sekme=<sekme>` derin bağlantısını okur (sürüm 2:
 * `/hastalar/<id>?sekme=exam` muayene sekmesini açar); yoldaki sekme sorgudan önce gelir ve
 * tanınmayan sekme yok sayılır.
 */
export function parsePath(pathname: string, search = ''): RouteState | null {
  const clean = pathname.replace(/\/+$/, '') || '/';
  if (clean === '/') return { ...HOME_ROUTE };
  const m = PATIENT_PATH_RE.exec(clean);
  if (m) {
    const asked = new URLSearchParams(search).get('sekme');
    const fromQuery = (PATIENT_TABS as readonly string[]).includes(asked ?? '') ? asked : null;
    return {
      page: 'patient',
      patientId: m[1]!,
      patientTab: m[2] ?? fromQuery ?? DEFAULT_PATIENT_TAB,
    };
  }
  const page = (Object.keys(PAGE_PATH) as Exclude<PageId, 'patient'>[]).find(
    (p) => PAGE_PATH[p] === clean,
  );
  return page ? { page, patientId: null, patientTab: DEFAULT_PATIENT_TAB } : null;
}

/**
 * Girişten sonra dönülecek adres. Yalnızca kabuğun TANIDIĞI bir yol kabul edilir ve yol
 * `pathFor` ile tablodan yeniden üretilir; sorgu dizesi bu yolun arkasına eklenir. Sonuç her
 * zaman aynı kökene göreli bir yoldur (`/ayarlar?sekme=laboratuvarlar`): `//kötü.site`,
 * `/\kötü.site` ya da `https://…` gibi girdiler tanınmaz ve Bugün'e düşer — açık yönlendirme yok.
 */
export function returnPathFrom(pathname: string, search: string): string {
  const route = parsePath(pathname) ?? HOME_ROUTE;
  const query = search.startsWith('?') && search.length > 1 ? search : '';
  return `${pathFor(route)}${query}`;
}

// ── A3b kimlik yolculuğu ────────────────────────────────────────────────────────────────────────
// Oturum gerektirmeyen ekranlar kabuğun DIŞINDA durur: menü, hasta verisi ve rol yoktur. Bu yüzden
// ayrı bir tablo ve ayrı bir çözümleyici (`parseAuthPath`); yukarıdaki `parsePath` yalnızca kabuğun
// sayfalarını bilir. Yollar Türkçedir çünkü kullanıcı adres çubuğunda görür (spec §11).

/** Kök adres. Kabuk oturum varken burayı `HOME_ROUTE`a (Bugün) çevirir. */
export const HOME_PATH = '/';

/**
 * Kliniğin herkese açık gizlilik politikası (WhatsApp birlikte kullanım, spec §7). Meta uygulamayı
 * yayımlamak için bu adresi ister ("Privacy Policy URL"); veri silme talimatı adresi de aynı
 * sayfanın `#veri-silme` bölümüdür. Hasta sayfaları (onam, anket) ve giriş kartı buraya bağlanır.
 */
export const PRIVACY_PATH = '/gizlilik';

/**
 * Kabuk dışında çizilen SABİT yollar (public-entry.tsx). Sıra önemlidir: `/randevu/afis`
 * `/randevu`'dan önce gelir. Burada durur çünkü bakım sayfası (bakim.html) da okur ve kabuğun ya
 * da genel sayfaların kodunu yüklememelidir. Bakım ekranı bu listedeki yollarda klinik içi
 * yönetici notunu GÖSTERMEZ (`maintenance/audience.ts`).
 */
export const PUBLIC_PATHS = ['/randevu/afis', '/randevu', '/bekleme-salonu', PRIVACY_PATH] as const;

/** Oturum gerektirmeyen sayfalar: kurulum, şifre yolculuğu, oturum/yetki bilgilendirmesi. */
export const AUTH_PATHS = {
  setup: '/kurulum',
  forgot: '/sifremi-unuttum',
  reset: '/sifre-sifirla',
  invite: '/sifre-belirle',
  sessionExpired: '/oturum-doldu',
  unauthorized: '/yetkisiz',
} as const;

/**
 * Hastanın ödeme bağlantısı: `/odeme/<belirteç>` (B5, spec §11).
 *
 * **H20 — `/odeme` dizesi bu depoda TEK yerde yazılır**: `@dentos/contracts`'ın
 * `PUBLIC_PAY_PATH` sabiti. Sunucu bağlantı URL'sini de oradan kurar
 * (`${origin}${PUBLIC_PAY_PATH}/${belirteç}`), yani hastanın eline geçen adres ile burada
 * çözülen adres aynı kaynaktan gelir; elle yazılan ikinci bir dize ikisini sessizce ayırırdı.
 *
 * Yol `AUTH_PATHS`'e GİRMEZ: kimlik ekranları kabuğun içinde çizilir, bu sayfa kabuğun
 * TAMAMEN dışında (`public-entry.tsx`, B4 kalıbı) — ziyaretçi klinik kabuğunun kodunu hiç
 * indirmez ve oturum akışına hiç düşmez.
 *
 * Belirteç ÇÖZÜLMEZ (`decodeURIComponent` yok): alfabesi base64url'dür ve kaçış istemez;
 * bozuk bir `%` dizisi çözücüyü fırlatır ve sayfa hiç çizilmeden beyaz kalırdı. Biçimi
 * tutmayan belirteç sunucunun temiz 404'üne düşer ve hasta ne yapacağını söyleyen bir ekran
 * görür — kabuğun genel "sayfa bulunamadı"sını değil. `.` ve `..` bölütleri yine de
 * reddedilir: istek adresinde normalize edilip başka bir uca çarparlardı.
 */
export function parsePayPath(pathname: string): { token: string } | null {
  return parseTokenPath(pathname, PUBLIC_PAY_PATH);
}

/**
 * Hastanın onam imzası bağlantısı: `/onam/<belirteç>` (spec §7.1 imza delili).
 *
 * Sunucu bu adresi `PUBLIC_CONSENT_PATH` ile kurar; burada da aynı sabit okunur (H20).
 * 2026-09-23'e kadar bu rota YOKTU: sunucu bağlantıyı üretiyordu, hasta açınca kabuğun
 * "sayfa bulunamadı" ekranı çıkıyordu ve onam ancak klinikte tabletle imzalanabiliyordu.
 * Belirteç ödeme yolundaki kuralların aynısıyla çözülür (çözme yok, `.`/`..` reddedilir).
 */
export function parseConsentPath(pathname: string): { token: string } | null {
  return parseTokenPath(pathname, PUBLIC_CONSENT_PATH);
}

/**
 * Hastanın memnuniyet anketi bağlantısı: `/anket/<belirteç>` (WhatsApp hazır, spec 3.4). Hasta
 * WhatsApp'taki "Değerlendir" düğmesiyle gelir; adresi sunucu `SURVEY_PATH` ile kurar (Meta
 * şablonunun URL düğmesi), burada da aynı sabit okunur (H20). Belirteç ödeme ve onam yollarının
 * kurallarıyla çözülür (çözme yok, `.`/`..` reddedilir).
 */
export function parseSurveyPath(pathname: string): { token: string } | null {
  return parseTokenPath(pathname, SURVEY_PATH);
}

/** Belirteçli genel yolların ortak çözümü; kural tek yerde durur. */
function parseTokenPath(pathname: string, prefix: string): { token: string } | null {
  const clean = pathname.replace(/\/+$/, '') || '/';
  if (!clean.startsWith(`${prefix}/`)) return null;
  const token = clean.slice(prefix.length + 1);
  if (token === '' || token.includes('/') || token === '.' || token === '..') return null;
  return { token };
}

export type AuthRoute =
  | { kind: 'setup'; token: string }
  | { kind: 'forgot' }
  | { kind: 'password'; token: string }
  | { kind: 'sessionExpired' }
  | { kind: 'unauthorized' }
  | { kind: 'notFound' };

/**
 * Adres çubuğunu kimlik rotasına çevirir; `null` = "burayı kabuk yönetir" (kök, giriş ve
 * `PAGE_PATH`teki her sayfa). Kabuğun da tanımadığı yol 404'tür — bu yüzden 404 kararı tek yerde,
 * iki tablonun birleşiminde verilir. `/sifre-sifirla` ile `/sifre-belirle` aynı ekrana çıkar:
 * hangisi olduğunu belirtecin amacı (`RESET` / `SET`) sunucudan gelir, adres değil.
 */
export function parseAuthPath(pathname: string, search = ''): AuthRoute | null {
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  const token = new URLSearchParams(search).get('token') ?? '';
  switch (path) {
    case AUTH_PATHS.setup:
      return { kind: 'setup', token };
    case AUTH_PATHS.forgot:
      return { kind: 'forgot' };
    case AUTH_PATHS.reset:
    case AUTH_PATHS.invite:
      return { kind: 'password', token };
    case AUTH_PATHS.sessionExpired:
      return { kind: 'sessionExpired' };
    case AUTH_PATHS.unauthorized:
      return { kind: 'unauthorized' };
    case HOME_PATH:
    case LOGIN_PATH:
      return null;
    default:
      return parsePath(path) ? null : { kind: 'notFound' };
  }
}

/**
 * Tek yönlendirme kapısı: adresi değiştirir ve dinleyen kabuğu uyandırır. Aynı adrese ikinci kez
 * gidilmez — 401 gibi arka arkaya gelen olaylar geçmişe yığın bırakmasın. Kabuğun kendi sayfa
 * geçişleri `useHistorySync`ten geçer; bu işlev kimlik ekranlarına giriş/çıkış içindir.
 */
export function navigate(path: string): void {
  if (`${window.location.pathname}${window.location.search}` === path) return;
  window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// D1 lab — Ayarlar → Laboratuvarlar derin bağlantısı. İş emri modalı laboratuvarı olmayan
// kliniği buraya yollar; `live-settings-page.tsx` sekmeyi sorgu dizesinden açar.
export const LABS_SETTINGS_TAB = 'laboratuvarlar';
export const LABS_SETTINGS_PATH = `/ayarlar?sekme=${LABS_SETTINGS_TAB}`;

// Teklif düzenleyici — Ayarlar → Fiyat listesi derin bağlantısı (spec §4.6.7). ⌘K girdisi ve
// sunucunun `PRICE_LIST_CODE_UNKNOWN` metni bu ekranı gösterir; `live-settings-page.tsx` sekmeyi
// sorgu dizesinden açar ve Ayarlar zaten açıkken gelen gezinmeyi de dinler.
export const PRICE_LIST_SETTINGS_TAB = 'fiyat-listesi';
export const PRICE_LIST_SETTINGS_PATH = `/ayarlar?sekme=${PRICE_LIST_SETTINGS_TAB}`;

// Sürüm 1 — Ayarlar › Roller ve yetkiler derin bağlantısı (spec §3.9; plan C121). Yetkilerim penceresi
// yöneticiyi buraya yollar; `live-settings-page.tsx` sekmeyi sorgu dizesinden açar.
export const ROLES_SETTINGS_TAB = 'roller';
